/**
 * RevenueCat (Apple In-App Purchase) wrapper for ShopLedger iOS.
 *
 * Why this exists
 * ---------------
 * Apple App Review rejected v1.0 / build 117 under guideline 3.1.1: a
 * multiplatform subscription app that ships on the App Store must use
 * Apple's IAP for any purchase that happens inside the iOS app. Two
 * rounds of "free stand-alone reader app" framing under 3.1.3(d) were
 * templated-rejected, so we pivoted to actually adding IAP via
 * RevenueCat (which wraps StoreKit 2 and handles receipt validation,
 * sub state, and webhooks for us).
 *
 * Architecture
 * ------------
 * - iOS native only. On web/PWA/Android we keep the existing Stripe
 *   billing flow (cheaper for us, same product); the SDK is a no-op
 *   off iOS.
 * - The web/Stripe path remains the source of truth for shop subs
 *   ("studios" subscribe via shopledger.app on a browser); RevenueCat
 *   is a parallel path for users whose first touch is the iOS app.
 *   Phase 3 wires up the dedupe webhook that prevents double-billing.
 * - Identifier is the Supabase `auth.users.id` UUID, set via
 *   `Purchases.logIn` on sign-in. RevenueCat then keys all entitlement
 *   queries / webhook deliveries off that.
 *
 * Entitlement / offering names live in RevenueCat dashboard, project
 * `78489eb2`. The wire-up reference is in
 * `Shop Ledger/secrets/README.md` and the auto-memory note
 * `reference_revenuecat.md`.
 */
import {
  Purchases,
  LOG_LEVEL,
  type PurchasesOffering,
  type PurchasesPackage,
  type CustomerInfo,
} from "@revenuecat/purchases-capacitor";
import { isNativeIOS } from "./platform";

/**
 * iOS Public SDK API key. Safe to commit — RevenueCat public SDK keys
 * are explicitly designed to ship inside the app binary; they only
 * grant read-only access to the customer's own subscription state.
 * Server-side authoritative ops require the secret API key (held in
 * Supabase function env vars).
 */
const IOS_PUBLIC_API_KEY = "appl_EOfetUnfcqGVIcmFOxqAwbYHVGy";

/**
 * Identifier we treat as "the user is paying". Both `solo_pro` and
 * `studio` products grant this entitlement; downstream code must check
 * `customerInfo.activeSubscriptions[]` (or the productIdentifier of
 * the active sub) to differentiate Solo Pro vs Studio tier.
 */
export const PREMIUM_ENTITLEMENT_ID = "premium";

/**
 * Default offering returned by `getOfferings()` — set in dashboard.
 * Contains two packages: `solo_pro` ($19/mo) and `studio` ($49/mo).
 */
export const DEFAULT_OFFERING_ID = "default";

/** Custom package identifiers inside the default offering. */
export const SOLO_PRO_PACKAGE_ID = "solo_pro";
export const STUDIO_PACKAGE_ID = "studio";

let configured = false;
let configuringPromise: Promise<void> | null = null;

/**
 * Configure the SDK once at app boot. Idempotent and concurrency-safe;
 * subsequent calls await the in-flight configuration if it's still
 * running. No-op on non-iOS platforms.
 */
export async function configureRevenueCat(): Promise<void> {
  if (configured) return;
  if (!isNativeIOS()) return;
  if (configuringPromise) return configuringPromise;

  configuringPromise = (async () => {
    try {
      // WARN level is RevenueCat's recommended production setting.
      // Bump to INFO when debugging IAP receipt issues.
      await Purchases.setLogLevel({ level: LOG_LEVEL.WARN });
      await Purchases.configure({ apiKey: IOS_PUBLIC_API_KEY });
      configured = true;
    } catch (err) {
      // Swallow — the SDK throws if e.g. native shell isn't ready.
      // Calls to identify/getCustomerInfo will retry configure.
      console.error("[revenuecat] configure failed:", err);
    } finally {
      configuringPromise = null;
    }
  })();

  return configuringPromise;
}

/**
 * Identify the current user to RevenueCat. Called from the auth state
 * listener whenever a Supabase session lands. Safe to call repeatedly
 * with the same userId; RevenueCat dedupes.
 */
export async function identifyRevenueCatUser(userId: string): Promise<void> {
  if (!isNativeIOS()) return;
  if (!userId) return;
  if (!configured) await configureRevenueCat();
  if (!configured) return; // configure failed; bail quietly
  try {
    await Purchases.logIn({ appUserID: userId });
  } catch (err) {
    console.error("[revenuecat] logIn failed:", err);
  }
}

/**
 * Drop the RevenueCat identity, returning the SDK to anonymous mode.
 * Called from the sign-out path so the next user on the same device
 * doesn't inherit the previous user's entitlement state.
 */
export async function clearRevenueCatUser(): Promise<void> {
  if (!isNativeIOS()) return;
  if (!configured) return;
  try {
    await Purchases.logOut();
  } catch (err) {
    // logOut throws when already anonymous — that's a no-op outcome,
    // not an error. Suppress to avoid log noise on fresh installs.
    if (
      typeof (err as { message?: string })?.message === "string" &&
      (err as { message: string }).message.toLowerCase().includes("anonymous")
    ) {
      return;
    }
    console.warn("[revenuecat] logOut:", err);
  }
}

/**
 * Returns true if the current customer has the `premium` entitlement
 * active. Cheap to call — RevenueCat caches customerInfo locally.
 */
export async function hasPremiumEntitlement(): Promise<boolean> {
  if (!isNativeIOS()) return false;
  if (!configured) await configureRevenueCat();
  if (!configured) return false;
  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    return Boolean(
      customerInfo.entitlements.active[PREMIUM_ENTITLEMENT_ID],
    );
  } catch (err) {
    console.error("[revenuecat] getCustomerInfo failed:", err);
    return false;
  }
}

/**
 * Returns the active product identifier (e.g. `shopledger.studio.monthly`)
 * for tier-specific gating, or null if the user has no active sub.
 */
export async function getActiveProductId(): Promise<string | null> {
  if (!isNativeIOS()) return null;
  if (!configured) await configureRevenueCat();
  if (!configured) return null;
  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    const active = customerInfo.activeSubscriptions ?? [];
    return active[0] ?? null;
  } catch (err) {
    console.error("[revenuecat] getActiveProductId failed:", err);
    return null;
  }
}

/**
 * Fetches the `default` offering from RevenueCat (the offering we
 * configured in dashboard with two packages: solo_pro + studio).
 * Returns null on web / Android / if the offering is unavailable.
 *
 * Each package on the offering has a `.product.priceString` field
 * already localized by Apple to the user's App Store region — never
 * hardcode pricing in the paywall UI; render that field instead.
 */
export async function getDefaultOffering(): Promise<PurchasesOffering | null> {
  if (!isNativeIOS()) return null;
  if (!configured) await configureRevenueCat();
  if (!configured) {
    console.error("[revenuecat] getDefaultOffering: SDK not configured");
    return null;
  }
  try {
    const result = await Purchases.getOfferings();
    const { current, all } = result;
    const offering = current ?? all[DEFAULT_OFFERING_ID] ?? null;
    // Verbose diagnostic — Apple's iPad review hit a "not available"
    // error here in build 118; we want to know exactly which slot is
    // empty when that recurs.
    if (!offering) {
      console.error("[revenuecat] getDefaultOffering: no offering returned", {
        hasCurrent: Boolean(current),
        availableOfferingIds: Object.keys(all ?? {}),
      });
    } else if (!offering.availablePackages || offering.availablePackages.length === 0) {
      console.error("[revenuecat] getDefaultOffering: offering has no packages", {
        offeringId: offering.identifier,
      });
    } else {
      console.log("[revenuecat] getDefaultOffering: loaded", {
        offeringId: offering.identifier,
        packageIds: offering.availablePackages.map((p) => p.identifier),
      });
    }
    return offering;
  } catch (err) {
    console.error("[revenuecat] getOfferings threw:", err);
    return null;
  }
}

/**
 * Purchase a package. Throws on user cancellation (which Apple
 * doesn't treat as a real error — callers should swallow the
 * `userCancelled === true` case rather than show an error toast).
 *
 * On success, returns the updated customerInfo so callers can
 * immediately re-check entitlements without an extra round trip.
 */
export interface PurchaseResult {
  customerInfo: CustomerInfo;
  productIdentifier: string;
}

export async function purchasePackage(
  pkg: PurchasesPackage,
): Promise<PurchaseResult> {
  if (!isNativeIOS()) {
    throw new Error("purchasePackage is iOS-only");
  }
  if (!configured) await configureRevenueCat();
  if (!configured) throw new Error("RevenueCat SDK not configured");
  // The Capacitor binding wraps StoreKit 2's purchase flow. Errors
  // bubble up with a `userCancelled` flag the caller can branch on.
  const result = await Purchases.purchasePackage({ aPackage: pkg });
  return {
    customerInfo: result.customerInfo,
    productIdentifier: result.productIdentifier,
  };
}

/**
 * Restore previously-bought subscriptions on the current Apple ID.
 * Required by Apple guideline 3.1.1 — the paywall must offer this
 * even if our backend already syncs entitlements automatically.
 */
export async function restorePurchases(): Promise<CustomerInfo> {
  if (!isNativeIOS()) {
    throw new Error("restorePurchases is iOS-only");
  }
  if (!configured) await configureRevenueCat();
  if (!configured) throw new Error("RevenueCat SDK not configured");
  const { customerInfo } = await Purchases.restorePurchases();
  return customerInfo;
}

/**
 * Helper: pick the package matching an identifier (`solo_pro` or
 * `studio`) from an offering. Returns null if the package isn't
 * present (which should never happen given the dashboard config but
 * we want safe rendering rather than a thrown error if it does).
 */
export function findPackageByIdentifier(
  offering: PurchasesOffering | null,
  identifier: string,
): PurchasesPackage | null {
  if (!offering) return null;
  return (
    offering.availablePackages.find((p) => p.identifier === identifier) ??
    null
  );
}
